import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'
import { zodResolver } from '@hookform/resolvers/zod'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { Button } from '@/modules/shared/components/button/Button'
import { useCopyToClipboard } from '@/modules/shared/hooks/useCopyToClipboard'
import { feedbackIsEmpty } from '@/modules/shared/utils/formErrorMessages'

const SentryFeedbackDialogFormSchema = z.object({
  name: z.string(),
  email: z.string(),
  message: z.string().min(1, feedbackIsEmpty()),
})

type SentryFeedbackDialogFormInputs = z.infer<typeof SentryFeedbackDialogFormSchema>

interface SentryFeedbackDialogProps {
  isOpen: boolean
  onClose: () => void
}

export function SentryFeedbackDialog({ isOpen, onClose }: SentryFeedbackDialogProps) {
  const { t } = useTranslation()
  const { user } = useSessionUser()
  const { onCopy, hasCopied } = useCopyToClipboard()
  const [isSending, setIsSending] = useState(false)
  const [feedbackSent, setFeedbackSent] = useState(false)
  const [feedbackEventId, setFeedbackEventId] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState(false)

  const defaultValues = {
    name: user?.fullName ?? '',
    email: user?.email ?? '',
    message: '',
  }

  const { handleSubmit, register, reset, watch, formState } = useForm<SentryFeedbackDialogFormInputs>({
    resolver: zodResolver(SentryFeedbackDialogFormSchema),
    defaultValues,
  })

  const message = watch('message')

  const onSubmit: SubmitHandler<SentryFeedbackDialogFormInputs> = async ({ name, email, message }) => {
    setIsSending(true)
    setSubmitError(false)

    try {
      const eventId = Sentry.captureFeedback({
        name,
        email,
        message,
        url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
      })

      const flushed = await Sentry.flush(5000)

      if (!flushed) {
        console.error('Error submitting feedback: Sentry.flush timed out')
        setSubmitError(true)
        return
      }

      setFeedbackEventId(eventId)
      setFeedbackSent(true)
      reset(defaultValues)
    } catch (error) {
      console.error('Error submitting feedback:', error)
      setSubmitError(true)
    } finally {
      setIsSending(false)
    }
  }

  const handleClose = () => {
    if (isSending) return

    reset(defaultValues)
    setFeedbackSent(false)
    setFeedbackEventId(null)
    setSubmitError(false)
    onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="modal-backdrop fixed inset-0" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <DialogTitle className="text-lg font-medium text-gray-800">
            {feedbackSent
              ? t('general.reportBugSuccess', 'Feedback Submitted')
              : t('general.reportABug', 'Report a Bug')}
          </DialogTitle>

          {!feedbackSent ? (
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="mt-4 space-y-4">
                <div>
                  <label htmlFor="feedbackName" className="block text-sm font-medium text-gray-700">
                    {t('general.name', 'Name')}
                  </label>
                  <input
                    type="text"
                    id="feedbackName"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none sm:text-sm"
                    {...register('name')}
                  />
                </div>

                <div>
                  <label htmlFor="feedbackEmail" className="block text-sm font-medium text-gray-700">
                    {t('general.email', 'Email')}
                  </label>
                  <input
                    type="email"
                    id="feedbackEmail"
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none sm:text-sm"
                    {...register('email')}
                  />
                </div>

                <div>
                  <label htmlFor="feedbackMessage" className="block text-sm font-medium text-gray-700">
                    {t('general.description', 'Description')} *
                  </label>
                  <textarea
                    id="feedbackMessage"
                    rows={4}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none sm:text-sm"
                    {...register('message')}
                  />
                  {formState.errors.message && (
                    <p className="mt-1 text-sm text-error">{formState.errors.message.message}</p>
                  )}
                </div>

                {submitError && (
                  <p className="text-sm text-error" data-testid="report-bug-submit-error">
                    {t(
                      'sentryFeedback.submitError',
                      'Unable to submit your report. Please try again, or disable your ad blocker for this site if the problem continues.'
                    )}
                  </p>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  type="button"
                  className="rounded-md bg-gray-200 px-4 py-2 text-sm shadow-sm hover:bg-gray-300"
                  onClick={handleClose}
                  disabled={isSending}
                >
                  {t('general.cancel', 'Cancel')}
                </Button>
                <Button
                  type="submit"
                  className="border border-primary/80 bg-primary px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  loading={isSending}
                  disabled={!message || isSending}
                >
                  {t('general.submit', 'Submit')}
                </Button>
              </div>
            </form>
          ) : (
            <div className="mt-4 space-y-4 text-center">
              <p className="text-gray-600">{t('sentryFeedback.thankYouForFeedback', 'Thank you for your feedback!')}</p>
              {feedbackEventId && (
                <div className="rounded-md bg-gray-50 px-4 py-3" data-testid="report-bug-reference-id">
                  <p className="text-sm text-gray-500">
                    {t('sentryFeedback.reportReference', 'Your report reference:')}
                  </p>
                  <p className="mt-1 break-all font-mono text-sm font-medium text-gray-800">{feedbackEventId}</p>
                  <Button
                    className="mt-3 rounded-md bg-gray-200 px-3 py-1.5 text-sm shadow-sm hover:bg-gray-300"
                    onClick={() => onCopy(feedbackEventId)}
                  >
                    {hasCopied ? t('general.copied', 'Copied') : t('general.copyToClipboard', 'Copy to Clipboard')}
                  </Button>
                </div>
              )}
              <div className="flex justify-center">
                <Button
                  className="border border-primary/80 bg-primary px-4 py-2 text-sm font-medium text-white shadow-md hover:bg-primary/80"
                  onClick={handleClose}
                >
                  {t('general.close', 'Close')}
                </Button>
              </div>
            </div>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
